<template>
  <div class="p-4 flex flex-col gap-6 w-full">
    <div>
      <div class="flex items-center gap-1 text-black-white text-l-medium h-9">
        <Icon icon="Hardware-Wireframe-Receiver" class="size-6" />
        {{ t('setting.pairing.selectTitle') }}
      </div>
      <div class="text-text-glass-default text-m-medium gap-1">
        {{ t('setting.pairing.selectDescription') }}
      </div>
    </div>

    <template v-if="step === 0">
      <div class="flex flex-col gap-8">
        <empty v-if="notDevices" />
        <selectDevice v-else v-model:selected-device="selectedDevice" />
        <div class="w-full flex items-center justify-center gap-2 text-l-medium">
          <ConnectDeviceButton>
            {{
              notDevices ? t('setting.pairing.connectDeviceBtn') : t('setting.pairing.addDeviceBtn')
            }}
          </ConnectDeviceButton>
          <Button class="px-16" color="black" :disabled="buttonDisabled" @click="handleNext">
            {{ t('setting.pairing.continueBtn') }}
          </Button>
        </div>
      </div>
    </template>
    <PairingDevice v-else :device="selectedDevice" @reset="reset" />
  </div>
</template>

<script lang="ts" setup>
  import { computed, onUnmounted, ref, watch } from 'vue'

  import { Button, Icon } from '@rapoo/element'
  import empty from './empty.vue'
  import selectDevice from './selectDevice.vue'
  import PairingDevice from './pairingDevice.vue'

  import { useCombinationKey } from '@/composables/useCombinationKey'

  import { useDeviceStore, useLocaleStore } from '@/stores'
  import { storeToRefs } from 'pinia'
  import { BaseDevice, ConnectionType } from '@rapoo/device-sdk'
  import ConnectDeviceButton from './ConnectDeviceButton.vue'

  const { t } = useLocaleStore()
  const deviceStore = useDeviceStore()
  const { deviceList } = storeToRefs(deviceStore)

  // 可选设备（仅有线设备可配对）
  const lineDevices = computed(() => {
    return deviceList.value.filter((item) => {
      const isWired = item.getConnectionType() === ConnectionType.Wired
      const pid = Array.isArray(item.manifest.hardware.productId)
        ? item.manifest.hardware.productId
        : [item.manifest.hardware.productId]
      const hasReceiver = pid.length > 1 || !!item.manifest.hardware.pairingProductIds?.length
      return isWired && hasReceiver
    })
  })

  // 空设备
  const notDevices = computed(() => {
    return lineDevices.value.length === 0
  })

  // 已选择的设备
  const selectedDevice = ref<BaseDevice | null>(null)
  watch(
    () => lineDevices.value,
    (val, _o) => {
      if (val.length) {
        // 未选择
        const unSelected = selectedDevice.value === null
        // 已选择的不在设备列表
        const selectedUnInList =
          selectedDevice.value && val.every((item) => item.id !== selectedDevice.value?.id)
        if (unSelected) {
          selectedDevice.value = val[0] as BaseDevice
        } else if (step.value === 0 && selectedUnInList) {
          selectedDevice.value = val[0] as BaseDevice
        }
      }
    },
    {
      immediate: true,
    },
  )

  // 下一步按钮是否禁用
  const buttonDisabled = computed(() => {
    if (notDevices.value) return true
    if (!selectedDevice.value) return true
    return false
  })

  // 下一步
  const { unregister } = useCombinationKey(
    'Enter',
    (e: KeyboardEvent) => {
      e.preventDefault()
      if (selectedDevice.value) {
        handleNext()
      }
    },
    { continuous: false },
  )
  onUnmounted(() => {
    unregister()
  })
  const step = ref(0) // 0 选择设备 1 配对设备
  const handleNext = () => {
    step.value = 1
  }

  // 重置
  const reset = () => {
    step.value = 0
    selectedDevice.value = null
  }
</script>
